import React, { useEffect, useState, useRef, Component } from 'react';
import { HeroSection } from '../components/HeroSection';
import { HowItWorks } from '../components/HowItWorks';
import { BenefitsSection } from '../components/BenefitsSection';
import { SituationsGrid } from '../components/SituationsGrid';
import { TestimonialsSection } from '../components/TestimonialsSection';
import { FAQSection } from '../components/FAQSection';
import { FinalCTA } from '../components/FinalCTA';
import { Footer } from '../components/Footer';
import { StickyMobileCTA } from '../components/StickyMobileCTA';
import { CashOfferForm } from '../components/CashOfferForm';
import { HomeIcon } from 'lucide-react';
// Reusable wrapper for the "bubbled off" sections
function BubbleSection({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={`relative overflow-hidden ${className}`} data-id="element-307">
      {/* Decorative Bubbles */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0" data-id="element-308">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-white/5 rounded-full blur-3xl" data-id="element-309" />
        <div className="absolute top-1/2 -left-20 w-72 h-72 bg-white/5 rounded-full blur-3xl" data-id="element-310" />
        <div className="absolute -bottom-40 right-1/4 w-80 h-80 bg-white/5 rounded-full blur-3xl" data-id="element-311" />
      </div>
      <div className="relative z-10" data-id="element-312">{children}</div>
    </div>;
}
// Animated Counter Component
function AnimatedCounter({
  end,
  suffix = ''
}: {
  end: number;
  suffix?: string;
}) {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setIsVisible(true);
    }, {
      threshold: 0.1
    });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    if (!isVisible) return;
    let startTimestamp: number;
    const duration = 1500;
    const step = (timestamp: number) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      // easeOutQuart
      const easeProgress = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(easeProgress * end));
      if (progress < 1) window.requestAnimationFrame(step);
    };
    window.requestAnimationFrame(step);
  }, [isVisible, end]);
  return <div ref={ref} className="text-4xl md:text-5xl font-extrabold mb-2" data-id="element-313">
      {count}
      {suffix}
    </div>;
}
export function LandingPage() {
  return <div className="min-h-screen bg-white font-sans text-slate-900 selection:bg-[#D4A24C] selection:text-white" data-id="element-314">
      <HeroSection data-id="element-315" />

      <BenefitsSection data-id="element-316" />
      <HowItWorks data-id="element-317" />
      <SituationsGrid data-id="element-318" />

      {/* Mid-page CTA Break */}
      <section className="py-24 relative overflow-hidden" data-id="element-319">
        <div className="absolute inset-0 z-0" data-id="element-320">
          <img src="https://images.unsplash.com/photo-1568605114967-8130f3a36994?auto=format&fit=crop&w=2000&q=80" alt="Typical Midwest single-family home" className="w-full h-full object-cover" data-id="element-321" />
          <div className="absolute inset-0 bg-[#0F172A]/80" data-id="element-322" />
        </div>
        <div className="max-w-4xl mx-auto px-6 relative z-10 text-center" data-id="element-323">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-8 leading-tight" data-id="element-324">
            Skip the repairs. Skip the showings. <br data-id="element-325" />
            Get cash for your house in days.
          </h2>
          <button onClick={() => {
          const form = document.getElementById('hero-form');
          form?.scrollIntoView({
            behavior: 'smooth'
          });
        }} className="inline-flex items-center bg-white text-[#0F172A] font-bold py-4 px-10 rounded-full hover:bg-slate-100 transition-colors text-lg shadow-xl relative" data-id="element-326">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#D4A24C] text-white text-xs font-bold uppercase tracking-wider py-1 px-3 rounded-full shadow-md whitespace-nowrap" data-id="element-327">
              Takes 60 Seconds
            </div>
            Get Your Cash Offer
          </button>
        </div>
      </section>

      <TestimonialsSection data-id="element-328" />
      <FAQSection data-id="element-329" />

      <BubbleSection className="bg-[#0F172A]" data-id="element-330">
        <FinalCTA data-id="element-331" />
      </BubbleSection>

      <Footer data-id="element-332" />
      <StickyMobileCTA data-id="element-333" />
    </div>;
}